import React from 'react';
import { CircularProgress, Switch, TextField } from '@mui/material';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import PopupBase from '../../../../../popup/base/PopupBase';
import CustomButton from '../../../../../components/buttons/CustomButton';
import styles from './RoomFormPopup.module.css';

const DESCRIPTION_MAX = 200;

/**
 * Criar ou editar uma sala: nome, descrição, capacidade e se está ativa.
 * Validação e envio ficam na página (CompanyRooms); Enter num campo envia.
 */
function RoomFormPopup({ isEdit, formData, errors, saving, onFieldChange, onSubmit, onClose }) {
    const descriptionLength = (formData.description || '').length;

    const content = (
        <form className={styles.content} onSubmit={onSubmit} noValidate>
            {errors.general && (
                <div className={styles.error} role="alert">{errors.general}</div>
            )}

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Identificação</h4>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="room-name">Nome da sala *</label>
                    <TextField
                        id="room-name"
                        value={formData.name}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                        disabled={saving}
                        fullWidth
                        size="small"
                        autoFocus={!isEdit}
                        placeholder="Ex.: Sala 1, Sala de avaliação"
                        error={!!errors.name}
                        helperText={errors.name}
                        className={styles.input}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="room-description">Descrição</label>
                    <TextField
                        id="room-description"
                        value={formData.description}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                        disabled={saving}
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        placeholder="Opcional. Equipamentos, andar, para que é usada"
                        error={!!errors.description}
                        helperText={errors.description || `${descriptionLength}/${DESCRIPTION_MAX}`}
                        className={`${styles.input} ${styles.counter}`}
                    />
                </div>
            </section>

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Uso</h4>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="room-capacity">Capacidade *</label>
                    <TextField
                        id="room-capacity"
                        type="number"
                        value={formData.capacity}
                        onChange={(e) => onFieldChange('capacity', e.target.value)}
                        disabled={saving}
                        size="small"
                        inputProps={{ min: 1, max: 100 }}
                        error={!!errors.capacity}
                        helperText={errors.capacity || 'Quantas pessoas a sala comporta ao mesmo tempo.'}
                        className={`${styles.input} ${styles.capacity}`}
                    />
                </div>
                <label className={styles.toggle}>
                    <span className={styles.toggleIcon}><ToggleOnOutlinedIcon fontSize="small" /></span>
                    <span className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Sala ativa</span>
                        <span className={styles.hint}>Desligue para tirar a sala de uso sem excluir.</span>
                    </span>
                    <Switch
                        checked={!!formData.is_active}
                        onChange={(e) => onFieldChange('is_active', e.target.checked)}
                        disabled={saving}
                        inputProps={{ 'aria-label': 'Sala ativa' }}
                    />
                </label>
            </section>

            {/* Enter num campo de uma linha envia o formulário */}
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
    );

    const footer = (
        <div className={styles.footer}>
            <CustomButton style="primary" variant="text" label="Cancelar" onClick={onClose} enabled={!saving} />
            <CustomButton
                style="main"
                variant="contained"
                label={isEdit ? 'Salvar' : 'Criar sala'}
                icon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={onSubmit}
                enabled={!saving}
            />
        </div>
    );

    return (
        <PopupBase
            Title={isEdit ? 'Editar sala' : 'Nova sala'}
            Content={content}
            Footer={footer}
            OnClose={onClose}
            maxWidth="sm"
        />
    );
}

export default RoomFormPopup;
